import React, { useState, useEffect } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import {
  makeStyles,
  Typography,
  Card,
  Box,
  Container,
  Link,
  Button
} from '@material-ui/core';

import Page from 'src/shared/ui/components/Page';
import Form from 'src/shared/ui/components/Form';
import processFormErrorMessages from 'src/shared/ui/components/Form/FormErrorMessage';

import validateSchema from 'src/shared/models/SchemaValidator';
import CustomerInvoice from 'src/customer-invoice/models/CustomerInvoice';

import getCustomerInvoiceById from 'src/customer-invoice/use-cases/getCustomerInvoiceById';
import updateCustomerInvoiceById from 'src/customer-invoice/use-cases/updateCustomerInvoiceById';
import getCustomerInvoiceStatusList from 'src/customer-invoice-status/use-cases/getCustomerInvoiceStatusList';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  saveButton: {
    top: '-20px'
  },
  backButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  scrollBox: {
    overflow: 'scroll',
    height: '80vh'
  }
}));

const CustomerInvoiceForm = ({ customerInvoiceId, cancel, success }) => {
  const classes = useStyles();

  const [formFields, setFormFields] = useState([]);
  const [invoiceStatusList, setInvoiceStatusList] = useState([]);

  const initFormFields = (fields, statusList) => {
    const instance = CustomerInvoice({
      fields,
      invoiceStatusList: statusList || invoiceStatusList
    });

    setFormFields(instance);
  };

  const fetchForm = async () => {
    const record = await getCustomerInvoiceById(customerInvoiceId);
    const statusList = await getCustomerInvoiceStatusList({
      page: null,
      limit: null,
      orderBy: ''
    });
    const statuses = statusList.rows || statusList;

    setInvoiceStatusList(statuses);
    initFormFields(record, statuses);
  };

  useEffect(() => {
    fetchForm();
  }, []);

  const validateFormFields = async (formFieldsValues) => {
    const validatedFormFields = await validateSchema(formFieldsValues);
    formFieldsValues = processFormErrorMessages(
      validatedFormFields,
      formFieldsValues
    );
    setFormFields(formFieldsValues.fields);

    return {
      errors: formFieldsValues.errors,
      fields: validatedFormFields
    };
  };

  const handleChangeFormFieldsValues = (formFieldsValues) => {
    setFormFields(formFieldsValues);
  };

  const postForm = async () => {
    const cancelledOption = invoiceStatusList.find(
      (s) => s.code === 'CANCELLED'
    );
    const selectedStatusId = formFields.find(
      (f) => f.name === 'customerInvoiceStatusId'
    )?.value;
    const isCancelling =
      cancelledOption && selectedStatusId === cancelledOption.id;

    if (isCancelling) {
      const confirmed = window.confirm(
        '¿Está seguro de que desea CANCELAR esta factura? Las cuotas asociadas se revertirán a pendientes. Esta acción no se puede deshacer.'
      );
      if (!confirmed) return;

      // Send minimal payload — only the status change needed to cancel
      let result;
      try {
        result = await updateCustomerInvoiceById(
          { customerInvoiceStatusId: cancelledOption.id },
          customerInvoiceId
        );
      } catch (e) {
        alert('Se ha producido un error, vuelva a intentarlo');
        return;
      }
      if (typeof result.id === 'undefined') {
        alert('Se ha producido un error, vuelva a intentarlo');
      } else {
        if (result.message) {
          alert(result.message);
        }
        success();
      }
      return;
    }

    let validatedForm = await validateFormFields(formFields);
    if (validatedForm.errors) {
      alert('Rellene todos los campos correctamente.');
      return;
    }

    let result;
    try {
      result = await updateCustomerInvoiceById(
        validatedForm.fields,
        customerInvoiceId
      );
    } catch (e) {
      alert('Se ha producido un error, vuelva a intentarlo');
      return;
    }
    if (typeof result.id === 'undefined') {
      alert('Se ha producido un error, vuelva a intentarlo');
    } else {
      if (result.message) {
        alert(result.message);
      }
      success();
    }
  };

  return (
    <Page className={classes.root} title="Cliente">
      <Container maxWidth={false}>
        <Box className={classes.navigation} display="flex">
          <Typography color="textSecondary" variant="body2">
            <Link component={RouterLink} to="" onClick={cancel}>
              Factura
            </Link>
            {` >  ${
              formFields.length && formFields[1].value.length
                ? formFields[1].value
                : ''
            }`}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <Button className={classes.backButton} onClick={cancel}>
            {'Volver'}
          </Button>

          <Button
            color="primary"
            variant="contained"
            className={classes.saveButton}
            onClick={postForm}
          >
            Guardar
          </Button>
        </Box>
        <Card>
          <Form
            fields={formFields}
            onChangeFields={handleChangeFormFieldsValues}
          />
        </Card>
      </Container>
    </Page>
  );
};

export default CustomerInvoiceForm;
